import { Component, Injector, OnInit } from '@angular/core';
import { PositionsApiService } from '@pages/positions/services/details/base/positions-api.service';
import { take } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PartnerDropdownItem } from '@pages/partners/classes/PartnerDropdownItem';
import { PartnerDropdownProject } from '@pages/partners/classes/PartnerDropdownProject';
import { PositionDropdownItem } from '@pages/positions/classes/PositionDropdownItem';
import { CandidatesService } from '@pages/candidates/services/candidates.service';
import { MatModalBaseComponent } from '@shared/modules/mat-modal/mat-modal-base.component';
import { CandidateAttachPositionModalData } from '@shared/modules/modals/candidate-modals/candidate-attach-position-modal/classes/candidate-attach-position-modal-config';
import { UserPositionType } from '@pages/positions/UserPositionType';
import { ToastService } from '@shared/modules/toast/services/toast.service';
import { UserPositionTypeOption } from '@pages/positions/classes/UserPositionTypeOption';

@UntilDestroy()
@Component({
  selector: 'app-candidate-attach-position-modal',
  templateUrl: './candidate-attach-position-modal.component.html',
  styleUrls: ['./candidate-attach-position-modal.component.scss'],
})
export class CandidateAttachPositionModalComponent
  extends MatModalBaseComponent<CandidateAttachPositionModalData>
  implements OnInit {
  form: FormGroup;
  candidateId: number;
  currentToastId: string;

  partners: PartnerDropdownItem[] = [];
  projects: PartnerDropdownProject[] = [];
  positions: PositionDropdownItem[] = [];

  readonly USER_POSITION_TYPE_OPTIONS = this.candidateService.getUserPositionTypeOptions();

  constructor(
    private positionApiService: PositionsApiService,
    private candidateService: CandidatesService,
    private formBuilder: FormBuilder,
    protected injector: Injector,
    private toastService: ToastService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.primaryButtonDisabled = true;
    this.form = this.initForm();
    this.loading = true;
    this.candidateId = this.data.candidateId;

    this.loadDependencies();
  }

  private loadDependencies() {
    const dependencies = {
      partners: this.positionApiService.getPartnersForDropdown().pipe(take(1)),
    };

    forkJoin(dependencies)
      .pipe(untilDestroyed(this))
      .subscribe(({ partners }) => {
        this.partners = partners;
        this.loading = false;

        this.listenFormChanges();
      });
  }

  private initForm(): FormGroup {
    return this.formBuilder.group({
      type: [null, [Validators.required]],
      partnerId: [null, [Validators.required]],
      projectId: [null, [Validators.required]],
      positionId: [null, [Validators.required]],
    });
  }

  private listenFormChanges(): void {
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.primaryButtonDisabled = this.form.invalid;
    });
  }

  attachToPosition() {
    this.primaryButtonDisabled = true;

    this.closeRestrictionMessage();

    this.candidateService
      .callAttachToPosition(
        this.candidateId,
        this.form.get('positionId').value as number,
        this.form.get('type').value as UserPositionType
      )
      .subscribe(this.handleConfirmActionResponse.bind(this));
  }

  onTypeChange(event: UserPositionTypeOption) {
    const supportingData = {
      messages: ['no_email', 'limited_status', 'no_gdpr', 'no_deletion'],
      title: 'candidates.user_position_types.restrictions.title',
    };

    if (this.currentToastId) {
      this.toastService.removeToast(this.currentToastId);
    }

    if (event.value === UserPositionType.Contact) {
      this.currentToastId = this.toastService
        .showInfo(
          supportingData.messages,
          {
            autoClose: false,
            duration: 0,
            position: 'top-right',
            className: 'custom-toast-container',
          },
          supportingData.title,
          'candidates.user_position_types.restrictions.messages'
        )
        .getToast().id;
    }
  }

  closeRestrictionMessage() {
    this.toastService.removeToast(this.currentToastId);
  }
}
